import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { CopyToClipboardIcon } from 'ui-components/components/copy-to-clipboard-button/copy-to-clipboard-icon';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import style from './copy-to-clipboard-button.module.less';
import { messages } from './messages';
import { copyToClipboard } from '../../../services/copy-to-clipboard';
// @ts-expect-error less import

/**
 * @typedef {import('./copy-to-clipboard-button-types.d.ts').CopyToClipboardButtonComponent} CopyToClipboardButtonComponent
 * @typedef {import('./copy-to-clipboard-button-types.d.ts').CopyToClipboardButtonProps} CopyToClipboardButtonProps
 */

/**
 * @type {React.FC<CopyToClipboardButtonProps>}
 */
export const CopyToClipboardButton = ({
  className,
  shouldUseLightTooltip = false,
  textToCopy,
  tooltipMessageDescriptor,
  tooltipPosition,
}) => {
  const [showCheckmark, setShowCheckmark] = useState(false);

  const { formatMessage } = useIntl();

  /** @type {() => void} */
  const copyTextToClipboard = useCallback(() => {
    copyToClipboard(textToCopy);
    setShowCheckmark(true);

    setTimeout(() => {
      setShowCheckmark(false);
    }, 1000);
  }, [textToCopy]);

  return showCheckmark ? (
    <Tooltip position={tooltipPosition}>
      <span className={classnames(style['copy-to-clipboard-button'], className)}>
        <FontAwesomeIcon className={style['copy-to-clipboard-button-icon']} icon={faCheck} />
      </span>
      <TooltipContent lightTheme={shouldUseLightTooltip}>
        <Text textType={TextType.Paragraph} value={messages.copiedToClipboard} />
      </TooltipContent>
    </Tooltip>
  ) : (
    <Tooltip position={tooltipPosition}>
      <span>
        <Button
          ariaLabel={formatMessage(tooltipMessageDescriptor)}
          className={classnames(style['copy-to-clipboard-button'], className)}
          onClick={copyTextToClipboard}
          theme='unstyled'
        >
          <CopyToClipboardIcon className={style['copy-to-clipboard-button-icon']} />
        </Button>
      </span>
      <TooltipContent lightTheme={shouldUseLightTooltip}>
        <Text textType={TextType.Paragraph} value={tooltipMessageDescriptor} />
      </TooltipContent>
    </Tooltip>
  );
};
